import { Box, styled, useTheme, Typography } from '@mui/material'
import { Skeleton } from '@mui/material'
import config from 'config'
import { EidAutocomplete } from 'packages/eid-controls'
import { ArrowIcon } from 'packages/eid-icons'
import { Avatar, WithCloseButton } from 'packages/eid-ui'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SelectAutocomplete from '../Components/AutoComplete/AutoComplete'
import FieldItemWrapper from '../Components/FieldItemWrapper'
import RequiredFieldErrorMessage from '../Components/FieldItemWrapper/RequiredFieldErrorMessage'
import classNames from 'classnames'
import { ACTION_TYPES, getFieldTypeRequiredErrorMessage } from 'packages/core'
import InfoMessageForWrongJson from '../Components/InfoMessageForWrongJson'

const MultiSelectStyles = styled('div')({
    width: 'auto',
})

const InputWrapper = styled('div')(({ theme }) => ({
    position: 'relative',
    border: '1px solid #ebebed',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0.4rem',
    padding: '0.1rem',
    display: 'flex',
    flexWrap: 'wrap',
    boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
    minWidth: '36rem',
    '& .MuiAutocomplete-tag': {
        height: '3rem',
    },
    '& input': {
        fontSize: '1.5rem',
        height: '3.5rem',
        lineHeight: 'normal',
        padding: '0.8rem',
        flexGrow: 1,
        border: 0,
        outline: 0,
        '&::placeholder': {
            color: '#b4b4b4',
        },

        '&:-ms-input-placeholder': {
            opacity: `${1} !important`,
            color: '#b4b4b4 !important',
        },
    },
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}))

const PersonRelativeWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
    '& p': {
        minWidth: '30.2rem',
    },
    '& input': {
        minWidth: '30.2rem',
    },
    '& .MuiFormControl-root': {
        marginTop: '0 !important',
    },
}))

const ErrorField = styled(Box)({
    outline: '1px solid #d0021b',
    borderRadius: '0.4rem',
})

const SelectedValueIndicator = styled(WithCloseButton)(({ theme }) => ({
    borderRadius: '0.4rem',
    backgroundColor: '#535b62',
    padding: '1rem 2.2rem 1rem 1.6rem',
    color: theme?.palette?.common?.white,
    display: 'flex',
    alignItems: 'center',
    minWidth: '36rem',
}))

const EndAdornmentContainer = styled('div')({
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    msTransform: 'translate(-50%, -50%)',
    top: '50%',
    right: '-0.2rem',
    display: 'flex',
    cursor: 'pointer',
})

const OptionComponent = EidAutocomplete.getOptionComponent({
    type: 'person',
    disableImage: true,
    labelProp: 'FriendlyName',
})

const PersonRelative = ({
    fieldType,
    isFormValidated,
    valueType,
    isApiSupportedSource,
    handleUpdate,
    useDataHook,
}) => {
    const theme = useTheme()
    const [sourceValues, setSourceValues] = useState([])
    const [isAssignedAsSource, setIsAssignedAsSource] = useState(false)
    const [showFieldValues, setShowFieldValues] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')
    const { t } = useTranslation()
    const [sourceUpdated, setSourceUpdated] = useState(false)

    useEffect(() => {
        if (fieldType?.SourceValues && sourceUpdated) {
            setShowFieldValues(
                fieldType?.SourceValues?.length ||
                    fieldType?.[valueType]?.length
                    ? true
                    : false,
            )
            setSourceValues(fieldType?.SourceValues || [])
        }
    }, [fieldType?.SourceValues, sourceUpdated])

    useEffect(() => {
        if (isFormValidated && !fieldType?.[valueType]) {
            setErrorMessage(getFieldTypeRequiredErrorMessage(fieldType))
        } else {
            setErrorMessage('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFormValidated, fieldType?.[valueType]])

    const { data: sourceData, isFetching } = useDataHook(fieldType)

    useEffect(() => {
        if (isApiSupportedSource) {
            if (!isFetching) {
                updateSourceValues()
                setSourceUpdated(true)
            }
        } else {
            setShowFieldValues(
                fieldType?.SourceValues?.length ||
                    fieldType?.[valueType]?.length
                    ? true
                    : false,
            )
            setSourceValues(
                fieldType?.SourceValues || fieldType?.[valueType] || [],
            )
            if (
                !fieldType?.SourceValues?.length &&
                fieldType?.[valueType]?.length
            ) {
                setIsAssignedAsSource(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApiSupportedSource, isFetching])

    useEffect(() => {
        if (isAssignedAsSource) {
            handleChange(fieldType?.[valueType] || [])
        }
    }, [isAssignedAsSource])

    const getOptionLabel = (option) =>
        option ? option.FriendlyName || option.ValueName : ''

    const updateSourceValues = () => {
        const valueToUpdate = {
            ...fieldType,
            SourceValues: sourceData?.data || [],
        }
        handleUpdate(valueToUpdate)
    }

    // TODO: Keeping old logic in case we need it later
    // const handleSelection = (selected) => {
    //     const valueToUpdate = {
    //         ...fieldType,
    //         [valueType]: selected,
    //     }
    //     handleUpdate(valueToUpdate)
    // }

    const handleChange = (value) => {
        try {
            const valueToUpdate = {
                ...fieldType,
                isModified: true,
                ActionType: ACTION_TYPES.ADDED,
                [valueType]: value.map((item) => {
                    return {
                        ...item,
                        ActionType: ACTION_TYPES.ADDED,
                    }
                }),
            }

            handleUpdate(valueToUpdate)
        } catch (err) {}
    }

    return (
        <FieldItemWrapper
            title={fieldType?.Name}
            isRequired={fieldType?.IsRequired}
            fieldTypeScope={fieldType?.FieldTypeScope}
        >
            <PersonRelativeWrapper
                className={classNames({
                    [ErrorField]: Boolean(errorMessage),
                })}
            >
                {isFetching ? (
                    <Fragment>
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                    </Fragment>
                ) : showFieldValues ? (
                    <Fragment>
                        {/* {fieldType?.[valueType] ? (
                            <Box>
                                <SelectedValueIndicator
                                    onClose={() => {
                                        handleSelection(null)
                                    }}
                                >
                                    <Box sx={{ marginRight: '2.1rem' }}>
                                        <Avatar
                                            src={`${config.BASE_EID_URL}${fieldType?.[valueType]?.imageThumbUrl}`}
                                        />
                                    </Box>
                                    <Typography
                                        sx={{
                                            wordBreak: 'break-all',
                                        }}
                                        variant="body1"
                                    >
                                        {fieldType?.[valueType]?.FriendlyName}
                                    </Typography>
                                </SelectedValueIndicator>
                            </Box>
                        ) : ( */}
                        <Box
                            sx={
                                fieldType?.IsReadOnly ||
                                (!fieldType?.SourceValues && isAssignedAsSource)
                                    ? {
                                          position: 'relative',
                                          opacity: 0.6,
                                          cursor: 'not-allowed',
                                          '&::before': {
                                              content: '""',
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              zIndex: 9999,
                                              pointerEvents: 'auto',
                                              cursor: 'not-allowed',
                                          },
                                          '& *': {
                                              pointerEvents: 'none',
                                              userSelect: 'none',
                                          },
                                      }
                                    : {}
                            }
                        >
                            <SelectAutocomplete
                                placeholder={
                                    fieldType?.[valueType]?.length > 0
                                        ? ''
                                        : t('Select')
                                }
                                optionComponent={OptionComponent}
                                value={fieldType?.[valueType] || []}
                                options={sourceValues}
                                getOptionLabel={getOptionLabel}
                                onChange={(_, value) => handleChange(value)}
                                labelProps="FriendlyName"
                                inputWrapperComponent={InputWrapper}
                                multiselect
                                inputWrapperStyleProps={MultiSelectStyles}
                                loading={isFetching}
                                inputStyleProps={{ width: '0' }}
                                disabled={
                                    fieldType?.IsReadOnly ||
                                    (!fieldType?.SourceValues &&
                                        isAssignedAsSource)
                                }
                                showInfoTooltip={true}
                            />
                        </Box>

                        {/* )} */}
                    </Fragment>
                ) : (
                    <InfoMessageForWrongJson />
                )}
            </PersonRelativeWrapper>
            <Box>
                {Boolean(errorMessage) && (
                    <RequiredFieldErrorMessage errorMsgKey={errorMessage} />
                )}
            </Box>
        </FieldItemWrapper>
    )
}

export default PersonRelative
